import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/lib/SiteContext";
import { api, gbp } from "@/lib/api";

export default function Shop() {
  const { site } = useSite();
  const [params, setParams] = useSearchParams();
  const selected = params.get("category") || "";
  const [products, setProducts] = useState(null);

  useEffect(() => {
    setProducts(null);
    api
      .get("/public/products", { params: selected ? { category: selected } : {} })
      .then((res) => setProducts(res.data))
      .catch(() => setProducts([]));
  }, [selected]);

  const categories = site?.categories || [];

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8" data-testid="shop-page">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Shop</p>
        <h1 className="mt-3 font-serif text-3xl font-bold tracking-tight text-cream-text sm:text-4xl lg:text-5xl">
          This Week's Bakes
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-cream-muted">
          Handmade to order every week. Ordering closes on Wednesday at 6:00pm for this week's
          delivery and collection dates.
        </p>
      </Reveal>

      <div className="mt-8 flex flex-wrap gap-2" role="group" aria-label="Filter by category" data-testid="shop-category-filters">
        <button
          onClick={() => setParams({})}
          className={`rounded-full px-4 py-2 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus ${
            !selected ? "bg-gold text-choco" : "border border-choco-line text-cream-muted hover:border-gold hover:text-gold"
          }`}
          data-testid="filter-all"
        >
          All bakes
        </button>
        {categories.map((c) => (
          <button
            key={c.slug}
            onClick={() => setParams({ category: c.slug })}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus ${
              selected === c.slug ? "bg-gold text-choco" : "border border-choco-line text-cream-muted hover:border-gold hover:text-gold"
            }`}
            data-testid={`filter-${c.slug}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {products === null && <p className="text-sm text-cream-muted" data-testid="shop-loading">Loading…</p>}
        {products !== null && products.length === 0 && (
          <p className="col-span-full rounded-3xl border border-choco-line bg-choco-light p-8 text-center text-sm text-cream-muted" data-testid="shop-empty">
            Our latest bakes are coming soon — please check back shortly.
          </p>
        )}
        {(products || []).map((p, i) => {
          const img = p.images?.[0];
          return (
            <Reveal key={p.id} delay={i * 0.05}>
              <Link
                to={`/shop/${p.slug}`}
                className="group block h-full overflow-hidden rounded-3xl bg-cream text-cream-darktext transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus"
                data-testid={`product-card-${p.slug}`}
              >
                <div className="relative overflow-hidden">
                  {img ? (
                    <img src={img.url_800 || img.url} alt={img.alt || p.name} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  ) : (
                    <div className="flex aspect-[4/3] w-full items-center justify-center bg-cream-subtle text-sm text-cream-darkmuted">Photo coming soon</div>
                  )}
                  {p.sold_out && (
                    <span className="absolute left-3 top-3 rounded-full bg-choco px-3 py-1 text-xs font-bold text-cream-text" data-testid={`sold-out-${p.slug}`}>
                      Sold out
                    </span>
                  )}
                  {p.orderable === false && !p.sold_out && (
                    <span className="absolute left-3 top-3 rounded-full bg-choco px-3 py-1 text-xs font-bold text-cream-text" data-testid={`coming-soon-${p.slug}`}>
                      Not yet available
                    </span>
                  )}
                  {p.featured && !p.sold_out && p.orderable !== false && (
                    <span className="absolute left-3 top-3 rounded-full bg-gold px-3 py-1 text-xs font-bold text-choco">Featured</span>
                  )}
                </div>
                <div className="p-6">
                  <h2 className="font-serif text-xl font-semibold">{p.name}</h2>
                  {p.short_description && (
                    <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-cream-darkmuted">{p.short_description}</p>
                  )}
                  <div className="mt-3 flex items-center justify-between">
                    <p className="text-base font-bold text-gold-hover" data-testid={`price-${p.slug}`}>
                      {gbp(p.from_price_pence)}
                      {p.sale_price_pence != null && (
                        <span className="ml-2 text-sm font-semibold text-cream-darkmuted line-through">{gbp(p.price_pence)}</span>
                      )}
                    </p>
                    {p.dietary_labels?.length > 0 && (
                      <span className="rounded-full border border-gold/50 px-2.5 py-0.5 text-[11px] font-bold text-gold-hover">
                        {p.dietary_labels[0]}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </div>
  );
}
